import { useRef, useEffect, useCallback, useMemo } from 'react';
import { WorkpaperProgress } from '../../../types/workpaper';
import { Chart, ChartData, ChartTypeRegistry, TooltipItem } from 'chart.js/auto';
import { formatDate, parseDate, parseLocalDate } from '../../../utilities/formatting';
import 'chartjs-adapter-date-fns';

function getFormattedData(workpaperProgress: WorkpaperProgress): {
    chartData: ChartData<keyof ChartTypeRegistry, { x: string; y: number | null }[]>;
    highestTaskNumber: number;
} {
    const totalTasksPerGroup = workpaperProgress.tasksComplete.map((t) => t.total);
    const totalWorkpaperTasks = totalTasksPerGroup.concat([workpaperProgress.totalWorkpaperTasks as number]);
    const highestTaskNumber = Math.max(...totalWorkpaperTasks);

    const completedTasks: { x: string; y: number | null }[] = [];
    for (const task of workpaperProgress.tasksComplete) {
        if (task.complete || task.complete === 0) {
            if (new Date().getTime() >= new Date(task.date).getTime()) {
                completedTasks.push({
                    x: parseLocalDate(task.date),
                    y: task.total - task.complete,
                });
            } else {
                completedTasks.push({ x: parseLocalDate(task.date), y: null });
            }
        } else if (parseDate(task.date) === parseDate(workpaperProgress.start)) {
            completedTasks.push({ x: parseLocalDate(task.date), y: task.total });
        }
    }

    const chartData = {
        labels: workpaperProgress.tasksComplete.map((t) => t.date),
        datasets: [
            {
                data: completedTasks,
                borderColor: '#4F46E5',
                backgroundColor: '#4F46E5',
                label: 'Remaining work',
                pointRadius: (data: { index: number; raw: { y: number | null } }): number => {
                    if (data.index === 0) {
                        return 3;
                    }
                    if (data.raw.y === completedTasks[data.index - 1].y) {
                        return 0;
                    }
                    return 3;
                },
                pointHitRadius: (data: { index: number; raw: { y: number | null } }): number => {
                    if (data.index === 0) {
                        return 3;
                    }
                    if (data.raw.y === completedTasks[data.index - 1].y) {
                        return 0;
                    }
                    return 3;
                },
                spanGaps: true,
            },
            {
                data: [
                    { x: parseLocalDate(workpaperProgress.start), y: highestTaskNumber },
                    { x: parseLocalDate(workpaperProgress.dueDate), y: 0 },
                ],
                borderColor: 'rgb(156 163 175)',
                backgroundColor: 'rgba(22, 163, 74, 0.2)',
                fill: true,
                label: 'Guideline',
                spanGaps: false,
            },
        ],
    };

    return { chartData, highestTaskNumber };
}

type BurndownChartProps = {
    workpaperProgress: WorkpaperProgress;
};

function BurndownChart({ workpaperProgress }: BurndownChartProps): JSX.Element {
    const { chartData, highestTaskNumber } = useMemo(() => {
        return getFormattedData(workpaperProgress);
    }, [workpaperProgress]);

    // use a ref to store the chart instance since it it mutable
    const chartRef = useRef<Chart<keyof ChartTypeRegistry, { x: string; y: number | null }[]> | null>(null);

    // callback creates the chart on the canvas element
    const canvasCallback = useCallback(
        (canvas: HTMLCanvasElement | null) => {
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                chartRef.current?.destroy();
                chartRef.current = new Chart(ctx, {
                    type: 'line',
                    data: chartData,
                    options: {
                        plugins: {
                            tooltip: {
                                callbacks: {
                                    title: function (tooltipItem: TooltipItem<keyof ChartTypeRegistry>[]): string {
                                        const { label } = tooltipItem[0].dataset;
                                        const date = tooltipItem[0].label.split(',');
                                        const currentDate = new Date(`${date[0]} ${date[1]}`);
                                        let formattedDate = '';
                                        if (label === 'Remaining work') {
                                            const nextDate = new Date(currentDate);
                                            nextDate.setDate(nextDate.getDate() + 6);
                                            formattedDate = `${formatDate(currentDate)} - ${formatDate(nextDate)}`;
                                        } else {
                                            formattedDate = formatDate(currentDate);
                                        }
                                        return formattedDate;
                                    },
                                },
                            },
                            legend: {
                                labels: { boxHeight: 5, boxWidth: 5, usePointStyle: true },
                            },
                        },
                        scales: {
                            x: {
                                type: 'time',
                                time: {
                                    unit: workpaperProgress.duration === 'monthly' ? 'month' : 'week',
                                },
                                ticks: {
                                    source: 'auto',
                                    showLabelBackdrop: true,
                                    maxRotation: 90,
                                    minRotation: 45,
                                    includeBounds: false,
                                },
                                min: parseLocalDate(workpaperProgress.start),
                                suggestedMax: parseLocalDate(workpaperProgress.endDate),
                                grid: { color: '' },
                            },
                            y: {
                                min: 0,
                                max: highestTaskNumber,
                            },
                        },
                        clip: false,
                    },
                });
            }
        },

        [chartData, highestTaskNumber, workpaperProgress.duration, workpaperProgress.start, workpaperProgress.endDate]
    );

    // effect to update the chart when props are updated
    useEffect(() => {
        if (chartRef.current) {
            chartRef.current.data = chartData;
            chartRef.current.update();
        }
    }, [chartData]);

    return (
        <div>
            <div className="flex justify-center">
                <div className="overflow-hidden">
                    <canvas width="500" height="300" ref={canvasCallback}></canvas>
                </div>
            </div>
        </div>
    );
}

export default BurndownChart;
